/**
 * Aligns vendor logos across product cards in each grid row
 * by padding shorter image areas. Batches layout reads/writes
 * and defers work via requestIdleCallback to reduce main-thread blocking.
 */
(function () {
  if (window.__collectionCardAlignBootstrapped) return;
  window.__collectionCardAlignBootstrapped = true;

  const SELECTOR = '.template-collection .product-grid.product-grid--grid';
  const ROW_TOLERANCE = 8;
  const DEBOUNCE_MS = 200;
  const IDLE_TIMEOUT_MS = 500;

  let grid = null;
  let rafId = null;
  let idleId = null;
  let debounceTimer = null;
  let alignQueued = false;

  function cancelScheduledAlign() {
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (idleId != null && 'cancelIdleCallback' in window) {
      cancelIdleCallback(idleId);
      idleId = null;
    }
  }

  function groupRowsByTop(itemData) {
    const rows = [];
    let currentRow = [];
    let currentTop = null;

    itemData.forEach((data) => {
      if (currentTop === null || Math.abs(data.top - currentTop) <= ROW_TOLERANCE) {
        currentRow.push(data);
        if (currentTop === null) currentTop = data.top;
        return;
      }

      if (currentRow.length) rows.push(currentRow);
      currentRow = [data];
      currentTop = data.top;
    });

    if (currentRow.length) rows.push(currentRow);
    return rows;
  }

  function alignCollectionCards() {
    if (!grid) return;
    alignQueued = false;

    const items = grid.querySelectorAll(':scope > .product-grid__item');
    if (!items.length) return;

    const medias = [];

    items.forEach((item) => {
      const media = item.querySelector('.card--block-media');
      if (media) {
        media.style.removeProperty('padding-bottom');
        medias.push(media);
      }
    });

    const itemData = [];

    items.forEach((item) => {
      const wrapper = item.querySelector('.product-card-wrapper');
      const vendor = item.querySelector('.card--block-vendor');
      const media = item.querySelector('.card--block-media');
      if (!wrapper || !vendor) return;

      itemData.push({
        media,
        top: item.offsetTop,
        vendorOffset: vendor.offsetTop - wrapper.offsetTop,
      });
    });

    if (!itemData.length) return;

    groupRowsByTop(itemData).forEach((rowItems) => {
      let maxVendorOffset = 0;

      rowItems.forEach((data) => {
        maxVendorOffset = Math.max(maxVendorOffset, data.vendorOffset);
      });

      if (maxVendorOffset <= 0.5) return;

      rowItems.forEach((data) => {
        if (!data.media) return;

        const diff = maxVendorOffset - data.vendorOffset;
        if (diff > 0.5) {
          data.media.style.paddingBottom = `${Math.round(diff)}px`;
        }
      });
    });
  }

  function runAlign() {
    cancelScheduledAlign();
    alignCollectionCards();
  }

  function scheduleAlign({ urgent = false } = {}) {
    if (alignQueued) return;
    alignQueued = true;
    cancelScheduledAlign();

    const queue = () => {
      rafId = requestAnimationFrame(runAlign);
    };

    if (urgent) {
      queue();
      return;
    }

    if ('requestIdleCallback' in window) {
      idleId = requestIdleCallback(queue, { timeout: IDLE_TIMEOUT_MS });
    } else {
      queue();
    }
  }

  function debouncedAlign() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => scheduleAlign(), DEBOUNCE_MS);
  }

  function prioritizeFirstRowImages() {
    if (!grid) return;

    const items = grid.querySelectorAll(':scope > .product-grid__item');
    if (!items.length) return;

    const firstTop = items[0].offsetTop;

    items.forEach((item) => {
      if (Math.abs(item.offsetTop - firstTop) > ROW_TOLERANCE) return;

      const img = item.querySelector('.card__media .media > img:first-of-type');
      if (!img) return;

      img.loading = 'eager';
      if ('fetchPriority' in img) img.fetchPriority = 'high';
    });
  }

  function initSecondaryHoverLoad() {
    if (!grid) return;

    grid.addEventListener(
      'mouseenter',
      (event) => {
        const cardMedia = event.target.closest('.card__media');
        if (!cardMedia || !grid.contains(cardMedia)) return;

        const secondary = cardMedia.querySelector('[data-secondary-src]');
        if (!secondary || secondary.dataset.loaded === 'true') return;

        secondary.src = secondary.dataset.secondarySrc;
        secondary.dataset.loaded = 'true';
      },
      true
    );
  }

  function onGridImageLoad(event) {
    if (event.target.tagName !== 'IMG' || !grid?.contains(event.target)) return;
    debouncedAlign();
  }

  function init() {
    grid = document.querySelector(SELECTOR);
    if (!grid) return;

    prioritizeFirstRowImages();
    initSecondaryHoverLoad();
    scheduleAlign();

    window.addEventListener('resize', debouncedAlign, { passive: true });
    window.addEventListener('load', () => scheduleAlign(), { once: true });
    grid.addEventListener('load', onGridImageLoad, true);

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList' && mutation.target === grid)) {
        prioritizeFirstRowImages();
        debouncedAlign();
      }
    });

    observer.observe(grid, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
